import { useEffect, useRef, useState } from 'react';
import type React from 'react';

import { Spinner } from '@change/ds-core-ui';

import { useI18n } from '@change-corgi/core-react/i18n';

import { ComposeSheet } from '../ComposeSheet';
import { useGenerateDecisionMakerMessageDraft } from '../hooks/useGenerateDecisionMakerMessageDraft';
import type { ContactDecisionMakerStageProps } from '../types';

/**
 * Screen 3, drafting sub-state (CHANGE-97532). Fires the draft mutation once and moves on.
 *
 * There is no error branch on purpose: the request resolves with a usable draft even when the prompt
 * executor fails, so the supporter always reaches the review screen. By this point they have already
 * written their story, and an error screen would throw that away.
 */
export const Drafting: React.FC<ContactDecisionMakerStageProps> = ({ decisionMaker, petitionId, flow }) => {
	const { translate } = useI18n();
	const generateDraft = useGenerateDecisionMakerMessageDraft();
	const { reasonId, story } = flow.data;
	const { draftReady, dismiss } = flow.actions;

	// Dropped once this stage instance is gone. The reducer's guard is stage-based, so it is not
	// enough on its own: dismiss mid-request, reopen, pick a different reason and draft again, and the
	// first request can still resolve into `drafting` and overwrite the new story's draft with the old
	// one. Tied to unmount rather than the effect body so a dependency change cannot drop a live
	// request. Re-armed on mount rather than only initialised, because StrictMode runs effects
	// mount → cleanup → mount on the same instance.
	const activeRef = useRef(true);
	useEffect(() => {
		activeRef.current = true;

		return () => {
			activeRef.current = false;
		};
	}, []);

	// Once per mount. The stage is only reachable from `story:write`.
	const requestedRef = useRef(false);
	useEffect(() => {
		if (requestedRef.current || !reasonId) return;
		requestedRef.current = true;

		void (async () => {
			const { draft, source } = await generateDraft({
				petitionId,
				decisionMakerId: decisionMaker.id,
				decisionMakerName: decisionMaker.displayName,
				reasonId,
				story,
			});
			if (!activeRef.current) return;
			draftReady(draft, source);
		})();
	}, [generateDraft, petitionId, decisionMaker.id, decisionMaker.displayName, reasonId, story, draftReady]);

	const heading = translate('corgi.contact_decision_maker.review.drafting_heading');

	// The live region mounts empty and fills a tick later. A region that already has content when it
	// enters the DOM is not a change, so screen readers either skip it or read it on top of the
	// dialog's own name — populating it afterwards is what makes it a single, reliable announcement.
	const [announcement, setAnnouncement] = useState('');
	useEffect(() => {
		const timeout = window.setTimeout(() => setAnnouncement(heading), 100);

		return () => window.clearTimeout(timeout);
	}, [heading]);

	return (
		<ComposeSheet
			isOpen
			fullHeightOnMobile
			onOpenChange={(isOpen) => {
				if (!isOpen) dismiss();
			}}
			eyebrow={translate('corgi.contact_decision_maker.review.eyebrow')}
			heading={heading}
		>
			<div className="flex justify-center py-12" data-testid="contact-decision-maker-drafting">
				{/* Decorative: the heading already says what is happening, and the live region below is what
				    announces it. Labelling the spinner too would have it read twice. */}
				<Spinner aria-hidden />
			</div>

			{/* The heading is inside the dialog's own name, which is announced on open — but the transition
			    from the story stage to this one is not, so it gets its own polite announcement.

			    `output` carries `role="status"` implicitly; jsx-a11y/prefer-tag-over-role rejects the
			    explicit form. `aria-live` stays explicit — not every screen reader applies the role's
			    implicit politeness. */}
			<output aria-live="polite" className="sr-only">
				{announcement}
			</output>
		</ComposeSheet>
	);
};
